import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Users, UserPlus, Trash2, Bike, Sparkles, Check, X, Loader as Loader2, ShieldCheck, Map as MapIcon } from 'lucide-react';

interface Manager {
  id: string;
  email: string;
  display_name?: string;
  allowed_rutas?: number[] | null;
  units?: { name: string } | null;
}

interface User {
  id: string;
  email: string;
  role: 'seller' | 'motoboy';
  ruta: number;
  created_at: string;
  unit_id?: string;
  display_name?: string;
  is_coringa?: boolean;
  allowed_rutas?: number[] | null;
  assigned_ruta?: number | null;
  coringa_updated_at?: string | null;
  units?: { name: string } | null;
}

const RUTA_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

export default function Sellers() {
  const [users, setUsers] = useState<User[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [savingManagerId, setSavingManagerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'seller' as 'seller' | 'motoboy',
    ruta: '1'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowFeedback, setRowFeedback] = useState<{ [id: string]: { type: 'ok' | 'err'; msg: string } }>({});

  const isAdmin = user?.role === 'admin';
  const canToggleCoringa = isAdmin;
  const canSwitchAssignedRuta = isAdmin || user?.role === 'manager';

  const coringaCount = users.filter(u => u.role === 'seller' && u.is_coringa).length;

  const formatStamp = (iso?: string | null) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchUsers();
    if (user?.role === 'admin') {
      fetchManagers();
    }
  }, [user]);

  const fetchManagers = async () => {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, display_name, allowed_rutas, units!users_unit_id_fkey(name)')
      .eq('role', 'manager')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error fetching managers:', error);
      return;
    }
    setManagers((data as any) || []);
  };

  const toggleManagerRuta = async (target: Manager, ruta: number) => {
    const current = target.allowed_rutas || [];
    const isSelected = current.includes(ruta);
    const next = isSelected
      ? current.filter(r => r !== ruta)
      : [...current, ruta].sort((a, b) => a - b);

    setSavingManagerId(target.id);
    try {
      const { data, error } = await supabase
        .from('users')
        .update({ allowed_rutas: next.length > 0 ? next : null })
        .eq('id', target.id)
        .select('id, allowed_rutas')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Sem retorno do servidor');
      setManagers(prev => prev.map(m => (m.id === target.id ? { ...m, allowed_rutas: data.allowed_rutas } : m)));
      showRowFeedback(target.id, 'ok', 'Atualizado');
    } catch (err: any) {
      console.error('Error updating manager rutas:', err);
      showRowFeedback(target.id, 'err', 'Erro ao atualizar.');
    } finally {
      setSavingManagerId(null);
    }
  };

  const fetchUsers = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('users')
        .select('*, units!users_unit_id_fkey(name)')
        .in('role', ['seller', 'motoboy'])
        .order('created_at', { ascending: false });

      if (user?.role === 'manager') {
        query = query.eq('unit_id', user.unit_id);
      }

      const { data, error } = await query;

      if (error) throw error;

      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const callCreateUserFn = async (payload: Record<string, unknown>, method: 'POST' | 'DELETE' = 'POST') => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) throw new Error('Sessão expirada. Faça login novamente.');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user`,
      {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    let body: any = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    if (!response.ok || body?.error) {
      throw new Error(body?.error || `Falha na requisição (${response.status})`);
    }
    return body;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await callCreateUserFn({
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        ruta: parseInt(newUser.ruta),
        unit_id: user?.unit_id,
      });

      setNewUser({
        email: '',
        password: '',
        role: 'seller',
        ruta: '1'
      });
      fetchUsers();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err?.message || 'Erro ao criar usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Tem certeza que deseja excluir este usuário?')) return;

    try {
      await callCreateUserFn({ action: 'delete', user_id: userId });
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err?.message || 'Erro ao excluir usuário');
    }
  };

  const showRowFeedback = (id: string, type: 'ok' | 'err', msg: string) => {
    setRowFeedback(prev => ({ ...prev, [id]: { type, msg } }));
    setTimeout(() => {
      setRowFeedback(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }, 2500);
  };

  const patchUser = async (id: string, patch: Partial<User>) => {
    setSavingId(id);
    try {
      const { data, error } = await supabase
        .from('users')
        .update(patch)
        .eq('id', id)
        .select('id, is_coringa, allowed_rutas, assigned_ruta, ruta, coringa_updated_at')
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Sem retorno do servidor');

      setUsers(prev => prev.map(u => (u.id === id ? { ...u, ...data } : u)));
      showRowFeedback(id, 'ok', 'Atualizado');
    } catch (err: any) {
      console.error('Error updating user:', err);
      showRowFeedback(id, 'err', err?.message?.includes('assigned_ruta_in_allowed')
        ? 'A ruta ativa precisa estar entre as rutas permitidas.'
        : 'Erro ao atualizar.');
    } finally {
      setSavingId(null);
    }
  };

  const toggleCoringa = async (target: User) => {
    if (!canToggleCoringa) return;
    if (target.role !== 'seller') return;

    const nextIsCoringa = !target.is_coringa;

    if (nextIsCoringa) {
      const initialAllowed = target.allowed_rutas && target.allowed_rutas.length > 0
        ? target.allowed_rutas
        : [target.ruta];
      const initialAssigned = target.assigned_ruta ?? target.ruta;
      await patchUser(target.id, {
        is_coringa: true,
        allowed_rutas: initialAllowed,
        assigned_ruta: initialAssigned,
      });
    } else {
      await patchUser(target.id, {
        is_coringa: false,
        allowed_rutas: null,
        assigned_ruta: null,
      });
    }
  };

  const toggleAllowedRuta = async (target: User, ruta: number) => {
    if (!canToggleCoringa || !target.is_coringa) return;
    const current = target.allowed_rutas || [];
    const isSelected = current.includes(ruta);
    let next = isSelected ? current.filter(r => r !== ruta) : [...current, ruta].sort((a, b) => a - b);

    if (next.length === 0) {
      showRowFeedback(target.id, 'err', 'Selecione pelo menos uma ruta.');
      return;
    }

    const nextAssigned = target.assigned_ruta && next.includes(target.assigned_ruta)
      ? target.assigned_ruta
      : next[0];

    await patchUser(target.id, { allowed_rutas: next, assigned_ruta: nextAssigned });
  };

  const setAssignedRuta = async (target: User, ruta: number) => {
    if (!canSwitchAssignedRuta || !target.is_coringa) return;
    if (target.assigned_ruta === ruta) return;
    await patchUser(target.id, { assigned_ruta: ruta });
  };

  const setSellerRuta = async (target: User, ruta: number) => {
    if (!(target.role === 'seller' || target.role === 'motoboy')) return;
    if (target.ruta === ruta) return;
    await patchUser(target.id, { ruta });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Gerenciar Usuários</h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        {isAdmin ? (
          <>
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold">Adicionar Novo Usuário</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                required
                minLength={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Usuário</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value as 'seller' | 'motoboy' }))}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="seller">Vendedor</option>
                <option value="motoboy">Motoboy</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ruta</label>
              <select
                value={newUser.ruta}
                onChange={(e) => setNewUser(prev => ({ ...prev, ruta: e.target.value }))}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {RUTA_OPTIONS.map(r => (
                  <option key={r} value={r}>Ruta {r}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 rounded-md text-white transition-colors ${
              isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Cadastrando...' : 'Cadastrar Usuário'}
          </button>
        </form>
          </>
        ) : (
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h2 className="text-base font-semibold text-gray-800">Gerenciar rutas da equipe</h2>
              <p className="text-sm text-gray-600 mt-1">
                Você pode trocar a ruta em que cada vendedor está trabalhando. A criação e a exclusão de
                usuários são realizadas apenas pelo administrador.
              </p>
            </div>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold">Rutas visíveis para gestores</h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Selecione quais rutas do escritório cada gestor pode visualizar. Se nenhuma ruta for marcada,
              o gestor enxerga todas as rutas.
            </p>
          </div>

          <div className="divide-y divide-gray-100">
            {managers.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">Nenhum gestor cadastrado.</div>
            ) : (
              managers.map((m) => {
                const allowed = m.allowed_rutas || [];
                const feedback = rowFeedback[m.id];
                const isSaving = savingManagerId === m.id;
                return (
                  <div key={m.id} className="p-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{m.display_name || m.email}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <MapIcon className="w-3.5 h-3.5" />
                        {m.units?.name || 'Sem unidade'}
                        <span className="mx-1 text-gray-300">•</span>
                        {allowed.length === 0 ? 'Vê todas as rutas' : `Vê ${allowed.length} ruta${allowed.length === 1 ? '' : 's'}`}
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-2 lg:items-end">
                      <div className="flex flex-wrap gap-1">
                        {RUTA_OPTIONS.map(r => {
                          const selected = allowed.includes(r);
                          return (
                            <button
                              key={r}
                              type="button"
                              onClick={() => toggleManagerRuta(m, r)}
                              disabled={isSaving}
                              className={`w-9 h-9 rounded-md text-xs font-semibold border transition-colors ${
                                selected
                                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-700'
                              }`}
                              title={selected ? `Ocultar Ruta ${r}` : `Permitir Ruta ${r}`}
                            >
                              {r}
                            </button>
                          );
                        })}
                      </div>
                      <div className="h-4">
                        {feedback && (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                            feedback.type === 'ok' ? 'text-emerald-700' : 'text-red-600'
                          }`}>
                            {feedback.type === 'ok' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            {feedback.msg}
                          </span>
                        )}
                        {isSaving && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Salvando...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Lista de Usuários</h2>
          </div>
          {canSwitchAssignedRuta && (
            <div className="hidden sm:flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200 font-semibold">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                {coringaCount} vendedor{coringaCount === 1 ? '' : 'es'} coringa{coringaCount === 1 ? '' : 's'} ativo{coringaCount === 1 ? '' : 's'}
              </span>
              <span className="text-gray-500">atende múltiplas rutas</span>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-mail</th>
                {user?.role === 'admin' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidade</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ruta / Coringa</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cadastro</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => {
                const isSeller = u.role === 'seller';
                const isCoringa = !!u.is_coringa;
                const allowed = u.allowed_rutas || [];
                const feedback = rowFeedback[u.id];
                const isSaving = savingId === u.id;

                return (
                  <tr key={u.id} className="align-top">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {isSeller ? (
                          <Users className="w-5 h-5 text-blue-600 mr-2" />
                        ) : (
                          <Bike className="w-5 h-5 text-green-600 mr-2" />
                        )}
                        <span className="text-sm text-gray-900">
                          {isSeller ? 'Vendedor' : 'Motoboy'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{u.display_name || u.email}</div>
                      {u.display_name && <div className="text-xs text-gray-500">{u.email}</div>}
                    </td>
                    {user?.role === 'admin' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{u.units?.name || '-'}</div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2 min-w-[280px]">
                        {isAdmin ? (
                          <>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold">
                                Ruta base: {u.ruta}
                              </span>
                              {isCoringa && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-800 text-xs font-semibold border border-amber-200">
                                  <Sparkles className="w-3 h-3" />
                                  Coringa - Ativa: Ruta {u.assigned_ruta ?? u.ruta}
                                </span>
                              )}
                              {isCoringa && u.coringa_updated_at && (
                                <span className="text-[11px] text-gray-500">
                                  Última troca: {formatStamp(u.coringa_updated_at)}
                                </span>
                              )}
                              {isSeller && (
                                <button
                                  type="button"
                                  onClick={() => toggleCoringa(u)}
                                  disabled={isSaving}
                                  className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                                    isCoringa
                                      ? 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                      : 'border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100'
                                  }`}
                                  title={isCoringa ? 'Remover coringa' : 'Marcar como coringa'}
                                >
                                  {isCoringa ? 'Remover coringa' : 'Tornar coringa'}
                                </button>
                              )}
                            </div>

                            {isCoringa && isSeller && (
                              <div className="space-y-1.5">
                                <div>
                                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Rutas permitidas</div>
                                  <div className="flex flex-wrap gap-1">
                                    {RUTA_OPTIONS.map(r => {
                                      const selected = allowed.includes(r);
                                      return (
                                        <button
                                          key={r}
                                          type="button"
                                          onClick={() => toggleAllowedRuta(u, r)}
                                          disabled={isSaving}
                                          className={`w-8 h-8 rounded-md text-xs font-semibold border transition-colors ${
                                            selected
                                              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                                              : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-700'
                                          }`}
                                          title={selected ? `Remover Ruta ${r}` : `Permitir Ruta ${r}`}
                                        >
                                          {r}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div>
                                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Ruta ativa agora</div>
                                  <div className="flex flex-wrap gap-1">
                                    {allowed.length === 0 ? (
                                      <span className="text-xs text-gray-400 italic">Selecione ao menos uma ruta permitida.</span>
                                    ) : (
                                      allowed.map(r => {
                                        const isActive = u.assigned_ruta === r;
                                        return (
                                          <button
                                            key={r}
                                            type="button"
                                            onClick={() => setAssignedRuta(u, r)}
                                            disabled={isSaving}
                                            className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                                              isActive
                                                ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400 hover:text-amber-700'
                                            }`}
                                          >
                                            Ruta {r}{isActive ? ' - ativa' : ''}
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Ruta de atuação</div>
                            <div className="flex flex-wrap gap-1">
                              {RUTA_OPTIONS.map(r => {
                                const isActive = u.ruta === r;
                                return (
                                  <button
                                    key={r}
                                    type="button"
                                    onClick={() => setSellerRuta(u, r)}
                                    disabled={isSaving || isActive}
                                    className={`w-9 h-9 rounded-md text-xs font-semibold border transition-colors ${
                                      isActive
                                        ? 'bg-blue-600 text-white border-blue-600 cursor-default'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-700'
                                    }`}
                                    title={isActive ? `Ruta atual: ${r}` : `Trocar para Ruta ${r}`}
                                  >
                                    {r}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {feedback && (
                          <div
                            className={`inline-flex items-center gap-1 text-xs font-medium ${
                              feedback.type === 'ok' ? 'text-emerald-700' : 'text-red-600'
                            }`}
                          >
                            {feedback.type === 'ok' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            {feedback.msg}
                          </div>
                        )}
                        {isSaving && (
                          <div className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Salvando...
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(u.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir usuário"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
